import { Injectable, NgZone } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import {
  MatSnackBar,
  MatSnackBarRef,
  SimpleSnackBar,
} from "@angular/material/snack-bar";

import { Router, NavigationEnd } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";
import { ApplicationService } from "../application/application.service";
import { ESnackbarAction, routeList } from "../shared/models";

@Injectable({
  providedIn: "root",
})
export class SharedStoreService {
  // State management
  private _state = new BehaviorSubject<any>(null);
  public state$ = this._state.asObservable();

  private destroyed$ = new Subject<void>();
  private snackbarRef: MatSnackBarRef<SimpleSnackBar> = null;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private applicationService: ApplicationService,
    private ngZone: NgZone,
    private dialog: MatDialog
  ) {}

  /**
   * The function `triggerNavigationAfterSubmit` handles navigation logic based on the `actionType`
   * parameter and toggles completion status for a specific step in an application process.
   * @param {string} actionType - The `actionType` parameter in the `triggerNavigationAfterSubmit`
   * function represents the type of action that was completed in the application process. It is used to
   * determine which step was completed and trigger the navigation accordingly and goes to the next step (see form-stepper.component.ts).
   * the `completedStepName` based on the `actionType`
   */
  triggerNavigationAfterSubmit(actionType: string) {
    let completedStepName: string;
    switch (actionType) {
      case "PERSONAL_INFO":
        completedStepName = `${routeList.application.children.personalInfo}`;
        break;
      case "PROGRAM_LIST":
        completedStepName = `${routeList.application.children.programList}`;
        break;
      case "ENGLISH_PROFICIENCY":
        completedStepName = `${routeList.application.children.englishProf}`;
        break;
      case "PROGRAM_CHOICES":
        completedStepName = `${routeList.application.children.programChoice}`;
        break;

      case "ACADEMIC_INFO":
        completedStepName = `${routeList.application.children.academicInfo}`;
        break;

      case "DOCUMENT_UPLOAD":
        completedStepName = `${routeList.application.children.documentUpload}`;
        break;

      case "INFO_RELEASE":
        completedStepName = `${routeList.application.children.infoRelease}`;
        break;

      case "REVIEW_SUBMIT":
        completedStepName = `${routeList.application.children.review}`;
        break;

      case "PAYMENT":
        completedStepName = `${routeList.application.children.payment}`;
        break;

      case "CHECK_PROMO_CODE":
        completedStepName = `${routeList.application.children.payment}`;
        break;
      default:
        break;
    }
    this.applicationService.toggleComplete(completedStepName);

    // Handle last step logic
    if (
      actionType !== "REVIEW_SUBMIT" &&
      actionType !== "PAYMENT" &&
      this.applicationService.lastStep >= 8
    ) {
      this.applicationService.lastStep = 7;
    }
  }

  // Open a snackbar with an error message
  openSnackbarError(
    message: string,
    actionName: string,
    callback?: () => void
  ) {
    this.ngZone.run(() => {
      this.snackbarRef = this.snackBar.open(message, actionName, {
        duration: actionName === ESnackbarAction.REFRESH ? null : 15000,
        panelClass: "error-snackbar",
      });
      this.snackbarRef.onAction().subscribe(() => {
        if (callback) callback();
      });
    });
  }

  // Close snackbar when the route changes
  closeSnackbarOnRouteChange() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.snackBar.dismiss();
      });
  }

  // Open an error dialog
  openErrorDialog(errorData: any) {
    this.ngZone.run(() => {});
  }

  // Handle state changes (to update the store)
  setState(newState: any) {
    this._state.next(newState);
  }

  // Reset files or state after successful upload
  resetUnUploadedFiles() {
    this.setState({ uploaded: false }); // example state reset
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.unsubscribe();
  }
}
